import { SecurityShieldIcon } from '@helpers/Constants/Icons';
import { SubscriptionConfirmationProps } from '@helpers/Models';
import { useState } from 'react';
import { ContactSupportModalModal } from './Modal/ContactSupportModal';

/**
 * SubscriptionFooter Component
 *
 * Displays the footer section for subscription and payment-related screens.
 *
 * Features:
 * - Shows a secure payment notice indicating that all transactions are processed via Stripe
 *   and no payment details are stored on the server.
 * - Provides quick access to "Terms and Conditions" and "Contact Support".
 * - Displays a subscription disclaimer about auto-renewal and cancellation policy.
 *
 * Behavior:
 * - The secure payment message is conditionally rendered based on `isFromAppSettings`.
 *
 * UI Elements:
 * - Security badge with payment assurance text
 * - Action buttons for terms and support
 * - Informational text about subscription policies
 *
 * Props:
 * @param {boolean} isFromAppSettings - Determines whether the secure payment section should be hidden
 *
 * Usage:
 * Place this component at the bottom of subscription, billing, or checkout pages
 * to inform users about payment security and subscription terms.
 *
 * @component
 * @returns {JSX.Element} Rendered subscription footer
 */
export const SubscriptionFooter: React.FC<SubscriptionConfirmationProps> = ({
  isFromAppSettings,
}) => {
  const [showContactForm, setShowContactForm] = useState<boolean>(false);

  return (
    <>
      {/* Footer */}
      <div className="flex flex-col items-center gap-3 text-sm mb-2 mt-auto">
        {showContactForm && (
          <ContactSupportModalModal onClose={() => setShowContactForm(false)} />
        )}
        {/* Secure Payment Processing Badge */}
        {isFromAppSettings != true && (
          <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg flex flex-row gap-3 p-4 items-center justify-center w-full">
            <span>
              <SecurityShieldIcon width={20} height={20} fill="#004834" />
            </span>
            <div className="flex flex-col gap-1">
              <span className="text-[#1A1A1A] font-medium">
                Secure Payment Processing
              </span>
              <span className="text-sm text-[#6B7280]">
                All payments are processed securely through Stripe. We never
                store your payment details on our servers.
              </span>
            </div>
          </div>
        )}
        <div className="flex gap-3 font-semibold mt-5">
          <button type="button" className="text-primary font-bold">
            Terms and Conditions
          </button>
          <span className="text-[#D1D5DB]">|</span>
          <button
            type="button"
            onClick={() => setShowContactForm(true)}
            className="text-primary font-bold"
          >
            Contact Support
          </button>
        </div>
        <span className="text-[#9CA3AF]">
          Cancel anytime. No long-term contracts. Your subscription will be
          automatically renewed unless cancelled.
        </span>
      </div>
    </>
  );
};
